import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import billService from "../services/billService";
import { generateBillPdf } from "../services/billPdfGenerator";
import { toast } from "react-toastify";

/**
 * Create a new bill
 */
export const createBill = createAsyncThunk(
  "bill/create",
  async (billData, { rejectWithValue }) => {
    try {
      const response = await billService.createBill(billData);
      let bill = response.bill;
      try {
        await generateBillPdf(bill._id);
        const refreshed = await billService.getBillById(bill._id);
        bill = refreshed?.bill ?? refreshed ?? bill;
      } catch (pdfErr) {
        console.error("Bill PDF generation failed:", pdfErr);
      }
      toast.success("Bill created successfully");
      return bill;
    } catch (error) {
      toast.error(error.message || "Failed to create bill");
      return rejectWithValue(error.message || "Failed to create bill");
    }
  }
);

/**
 * Get all bills
 */
export const getAllBills = createAsyncThunk(
  "bill/getAll",
  async (filters = {}, { rejectWithValue }) => {
    try {
      const response = await billService.getAllBills(filters);
      return response;
    } catch (error) {
      toast.error(error.message || "Failed to fetch bills");
      return rejectWithValue(error.message || "Failed to fetch bills");
    }
  }
);

/**
 * Get single bill by ID
 */
export const getBillById = createAsyncThunk(
  "bill/getById",
  async (billId, { rejectWithValue }) => {
    try {
      const response = await billService.getBillById(billId);
      return response.bill;
    } catch (error) {
      toast.error(error.message || "Failed to fetch bill");
      return rejectWithValue(error.message || "Failed to fetch bill");
    }
  }
);

/**
 * Update bill
 */
export const updateBill = createAsyncThunk(
  "bill/update",
  async ({ billId, billData }, { rejectWithValue }) => {
    try {
      const response = await billService.updateBill(billId, billData);
      let bill = response.bill;
      try {
        await generateBillPdf(bill._id);
        const refreshed = await billService.getBillById(bill._id);
        bill = refreshed?.bill ?? refreshed ?? bill;
      } catch (pdfErr) {
        console.error("Bill PDF regeneration failed:", pdfErr);
      }
      toast.success("Bill updated successfully");
      return bill;
    } catch (error) {
      toast.error(error.message || "Failed to update bill");
      return rejectWithValue(error.message || "Failed to update bill");
    }
  }
);

/**
 * Delete bill
 */
export const deleteBill = createAsyncThunk(
  "bill/delete",
  async (billId, { rejectWithValue }) => {
    try {
      await billService.deleteBill(billId);
      toast.success("Bill deleted successfully");
      return billId;
    } catch (error) {
      toast.error(error.message || "Failed to delete bill");
      return rejectWithValue(error.message || "Failed to delete bill");
    }
  }
);

// Initial state
const initialState = {
  bills: [],
  currentBill: null,
  loading: false,
  error: null,
  createLoading: false,
  createSuccess: false,
  updateLoading: false,
  updateSuccess: false,
};

// Slice
const billSlice = createSlice({
  name: "bill",
  initialState,
  reducers: {
    clearCurrentBill: (state) => {
      state.currentBill = null;
    },
    resetCreate: (state) => {
      state.createSuccess = false;
    },
    resetUpdate: (state) => {
      state.updateSuccess = false;
    },
    clearErrors: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Create Bill
      .addCase(createBill.pending, (state) => {
        state.createLoading = true;
        state.createSuccess = false;
        state.error = null;
      })
      .addCase(createBill.fulfilled, (state, action) => {
        state.createLoading = false;
        state.createSuccess = true;
        state.bills = [action.payload, ...state.bills];
        state.error = null;
      })
      .addCase(createBill.rejected, (state, action) => {
        state.createLoading = false;
        state.createSuccess = false;
        state.error = action.payload;
      })
      // Get All Bills
      .addCase(getAllBills.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllBills.fulfilled, (state, action) => {
        state.loading = false;
        state.bills = action.payload.bills || [];
        state.error = null;
      })
      .addCase(getAllBills.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Get Bill By ID
      .addCase(getBillById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getBillById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentBill = action.payload;
        state.error = null;
      })
      .addCase(getBillById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update Bill
      .addCase(updateBill.pending, (state) => {
        state.updateLoading = true;
        state.updateSuccess = false;
        state.error = null;
      })
      .addCase(updateBill.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.updateSuccess = true;
        state.bills = state.bills.map((bill) =>
          bill._id === action.payload._id ? action.payload : bill
        );
        state.currentBill = action.payload;
        state.error = null;
      })
      .addCase(updateBill.rejected, (state, action) => {
        state.updateLoading = false;
        state.updateSuccess = false;
        state.error = action.payload;
      })
      // Delete Bill
      .addCase(deleteBill.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteBill.fulfilled, (state, action) => {
        state.loading = false;
        state.bills = state.bills.filter(
          (bill) => bill._id !== action.payload
        );
        state.error = null;
      })
      .addCase(deleteBill.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearCurrentBill, resetCreate, resetUpdate, clearErrors } =
  billSlice.actions;
export default billSlice.reducer;
